import { createRouter, createWebHistory } from 'vue-router'
import { defineAsyncComponent } from 'vue'
import { wechatLogin } from '@/api/wechat'
import store from '@/store'
import { Toast } from 'vant'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import ErrorFallback from '@/components/ErrorFallback.vue'

// 创建异步组件包装器，添加错误处理和加载状态
const createAsyncComponent = (importFunc) => {
  return defineAsyncComponent({
    loader: importFunc,
    loadingComponent: LoadingSpinner,
    errorComponent: ErrorFallback,
    delay: 200, // 延迟显示加载组件
    timeout: 10000, // 10秒超时
    suspensible: false,
    onError(error, retry, fail, attempts) {
      console.error(`Component loading failed (attempt ${attempts}):`, error)
      if (attempts <= 3) {
        // 重试最多3次
        retry()
      } else {
        fail()
      }
    }
  })
}

// 路由SEO meta配置
const routeMeta = {
  Home: {
    title: '蒙古丽人—让蒙古服饰走向世界_蒙古族服饰女装定制',
    description: '蒙古丽人专注蒙古族服饰、蒙古族女装设计与定制，涵盖蒙式传统蒙古袍、现代改良女装、婚庆礼服等。定制热线：133 4714 1793',
    keywords: '蒙古丽人,蒙古族服饰,蒙古族女装,蒙式,蒙古袍,蒙古服饰定制'
  },
  News: {
    title: '蒙古族服饰资讯_蒙式穿搭与文化_蒙古丽人',
    description: '蒙古丽人蒙古族服饰资讯，分享蒙式穿搭技巧、蒙古族服饰文化、传统工艺与时尚潮流，了解最新蒙古族女装动态。',
    keywords: '蒙古族服饰资讯,蒙式穿搭,蒙古族服饰文化,蒙古丽人资讯'
  },
  Category: {
    title: '蒙古族服饰分类_蒙式女装男装配饰_蒙古丽人',
    description: '蒙古丽人蒙古族服饰分类，涵盖蒙式女装、男装、配饰、传统蒙古袍、现代改良服饰等全品类蒙古族服饰。',
    keywords: '蒙古族服饰分类,蒙式女装,蒙古族男装,蒙古族配饰,蒙古丽人'
  },
  Products: {
    title: '蒙古族服饰商品列表_蒙式女装定制_蒙古丽人',
    description: '蒙古丽人蒙古族服饰商品列表，精选蒙式女装、传统蒙古袍、婚庆礼服、日常改良服饰等，支持量身定制。',
    keywords: '蒙古族服饰,蒙式女装,蒙古袍定制,蒙古族女装,蒙古丽人商城'
  },
  ProductDetail: {
    title: '蒙古丽人-商品详情',
    description: '蒙古丽人蒙古族服饰商品详情，高品质蒙式女装、传统蒙古袍等，支持定制。',
    keywords: '蒙古族服饰,蒙古族女装,蒙式,蒙古丽人'
  },
  NewsDetail: {
    title: '资讯详情_蒙古族服饰文化_蒙古丽人',
    description: '蒙古丽人蒙古族服饰文化资讯详情，了解蒙式传统与现代时尚。',
    keywords: '蒙古族服饰文化,蒙式资讯,蒙古丽人'
  }
}

const routes = [
  {
    path: '/',
    name: 'Home',
    component: createAsyncComponent(() => import('../views/Home.vue')),
    meta: { seo: routeMeta.Home }
  },
  {
    path: '/news',
    name: 'News',
    component: createAsyncComponent(() => import('../views/News.vue')),
    meta: { seo: routeMeta.News }
  },
  {
    path: '/category',
    name: 'Category',
    component: createAsyncComponent(() => import('../views/Category.vue')),
    meta: { seo: routeMeta.Category }
  },
  {
    path: '/news/:id',
    name: 'NewsDetail',
    component: createAsyncComponent(() => import('../views/NewsDetail.vue')),
    meta: { seo: routeMeta.NewsDetail }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: createAsyncComponent(() => import('../views/Profile.vue')),
    meta: { seo: { title: '个人中心_蒙古丽人', description: '蒙古丽人个人中心，管理订单、地址与定制需求。', keywords: '蒙古丽人,个人中心' } }
  },
  {
    path: '/workflow/publish',
    name: 'WorkflowPublish',
    component: createAsyncComponent(() => import('../views/workflow/WorkflowPublish.vue')),
    meta: { seo: { title: '发布工单_蒙古丽人', description: '蒙古丽人工单发布。', keywords: '蒙古丽人,工单' } }
  },
  {
    path: '/workflow/mine',
    name: 'WorkflowMine',
    component: createAsyncComponent(() => import('../views/workflow/WorkflowList.vue')),
    props: { type: 'mine' },
    meta: { seo: { title: '我的工单_蒙古丽人', description: '蒙古丽人我的工单。', keywords: '蒙古丽人,工单' } }
  },
  {
    path: '/workflow/todo',
    name: 'WorkflowTodo',
    component: createAsyncComponent(() => import('../views/workflow/WorkflowList.vue')),
    props: { type: 'todo' },
    meta: { seo: { title: '待办工单_蒙古丽人', description: '蒙古丽人待办工单。', keywords: '蒙古丽人,工单' } }
  },
  {
    path: '/workflow/done',
    name: 'WorkflowDone',
    component: createAsyncComponent(() => import('../views/workflow/WorkflowList.vue')),
    props: { type: 'done' },
    meta: { seo: { title: '已完成工单_蒙古丽人', description: '蒙古丽人已完成工单。', keywords: '蒙古丽人,工单' } }
  },
  {
    path: '/workflow/detail/:id',
    name: 'TaskDetail',
    component: createAsyncComponent(() => import('../views/workflow/TaskDetail.vue')),
    meta: { seo: { title: '工单详情_蒙古丽人', description: '蒙古丽人工单详情。', keywords: '蒙古丽人,工单' } }
  },
  {
    path: '/products',
    name: 'Products',
    component: createAsyncComponent(() => import('../views/Products.vue')),
    meta: { seo: routeMeta.Products }
  },
  {
    path: '/product-detail',
    name: 'ProductDetail',
    component: createAsyncComponent(() => import('../views/ProductDetail.vue')),
    meta: { seo: routeMeta.ProductDetail }
  },
  {
    path: '/customize-form',
    name: 'CustomizeForm',
    component: createAsyncComponent(() => import('../views/CustomizeForm.vue')),
    meta: { seo: { title: '蒙古族服饰定制_蒙式量身定制_蒙古丽人', description: '蒙古丽人蒙古族服饰量身定制，填写定制需求，打造专属蒙式女装。', keywords: '蒙古族服饰定制,蒙式定制,蒙古丽人' } }
  },
  {
    path: '/customize-agreement',
    name: 'CustomizeAgreement',
    component: createAsyncComponent(() => import('../views/CustomizeAgreement.vue')),
    meta: { seo: { title: '定制协议_蒙古丽人', description: '蒙古丽人服饰定制协议与须知。', keywords: '蒙古丽人,定制协议' } }
  },
  {
    path: '/product-customize',
    name: 'ProductCustomize',
    component: createAsyncComponent(() => import('../views/ProductCustomize.vue')),
    meta: { seo: { title: '商品定制_蒙古族服饰_蒙古丽人', description: '蒙古丽人蒙古族服饰商品定制。', keywords: '蒙古族服饰定制,蒙古丽人' } }
  },
  {
    path: '/order-confirm',
    name: 'OrderConfirm',
    component: createAsyncComponent(() => import('../views/OrderConfirm.vue')),
    meta: { seo: { noIndex: true, title: '确认订单_蒙古丽人' } }
  },
  {
    path: '/order-create',
    name: 'OrderCreate',
    component: createAsyncComponent(() => import('../views/OrderCreate.vue')),
    meta: { seo: { noIndex: true, title: '创建订单_蒙古丽人' } }
  },
  {
    path: '/order-success',
    name: 'OrderSuccess',
    component: createAsyncComponent(() => import('../views/OrderSuccess.vue')),
    meta: { seo: { noIndex: true, title: '下单成功_蒙古丽人' } }
  },
  {
    path: '/order-list',
    name: 'OrderList',
    component: createAsyncComponent(() => import('../views/OrderList.vue')),
    meta: { seo: { noIndex: true, title: '我的订单_蒙古丽人' } }
  },
  {
    path: '/order-status',
    name: 'OrderStatus',
    component: createAsyncComponent(() => import('../views/OrderStatus.vue')),
    meta: { seo: { noIndex: true, title: '订单状态_蒙古丽人' } }
  },
  {
    path: '/pending-payment',
    name: 'PendingPayment',
    component: createAsyncComponent(() => import('../views/PendingPayment.vue')),
    meta: { seo: { noIndex: true, title: '待付款_蒙古丽人' } }
  },
  {
    path: '/pending-shipment',
    name: 'PendingShipment',
    component: createAsyncComponent(() => import('../views/PendingShipment.vue')),
    meta: { seo: { noIndex: true, title: '待发货_蒙古丽人' } }
  },
  {
    path: '/pending-receive',
    name: 'PendingReceive',
    component: createAsyncComponent(() => import('../views/PendingReceive.vue')),
    meta: { seo: { noIndex: true, title: '待收货_蒙古丽人' } }
  },
  {
    path: '/completed-orders',
    name: 'CompletedOrders',
    component: createAsyncComponent(() => import('../views/CompletedOrders.vue')),
    meta: { seo: { noIndex: true, title: '已完成订单_蒙古丽人' } }
  },
  {
    path: '/order-detail',
    name: 'OrderDetail',
    component: createAsyncComponent(() => import('../views/OrderDetail.vue')),
    meta: { seo: { noIndex: true, title: '订单详情_蒙古丽人' } }
  },
  {
    path: '/logistics-detail',
    name: 'LogisticsDetail',
    component: createAsyncComponent(() => import('../views/LogisticsDetail.vue')),
    meta: { seo: { noIndex: true, title: '物流详情_蒙古丽人' } }
  },
  {
    path: '/payment',
    name: 'Payment',
    component: createAsyncComponent(() => import('../views/Payment.vue')),
    meta: { seo: { noIndex: true, title: '支付_蒙古丽人' } }
  },
  {
    path: '/mock-login',
    name: 'MockWechatLogin',
    component: createAsyncComponent(() => import('../views/MockWechatLogin.vue')),
    meta: { seo: { noIndex: true, title: '登录_蒙古丽人' } }
  },
  {
    path: '/address-manage',
    name: 'AddressManage',
    component: createAsyncComponent(() => import('../views/AddressManage.vue')),
    meta: { seo: { noIndex: true, title: '地址管理_蒙古丽人' } }
  },
  {
    path: '/address-edit',
    name: 'AddressEdit',
    component: createAsyncComponent(() => import('../views/AddressEdit.vue')),
    meta: { seo: { noIndex: true, title: '编辑地址_蒙古丽人' } }
  },
  {
    path: '/address-select',
    name: 'AddressSelect',
    component: createAsyncComponent(() => import('../views/AddressSelect.vue')),
    meta: { seo: { noIndex: true, title: '选择地址_蒙古丽人' } }
  },
  {
    path: '/button-demo',
    name: 'ButtonDemo',
    component: createAsyncComponent(() => import('../views/ButtonDemo.vue')),
    meta: { seo: { noIndex: true } }
  },
  {
    path: '/share',
    name: 'SharePage',
    component: createAsyncComponent(() => import('../views/SharePage.vue')),
    meta: { seo: { title: '分享_蒙古丽人', description: '分享蒙古丽人蒙古族服饰。', keywords: '蒙古丽人,蒙古族服饰,蒙式' } }
  },
  {
    path: '/wechat-entry-test',
    name: 'WechatEntryTest',
    component: createAsyncComponent(() => import('../views/WechatEntryTest.vue')),
    meta: { seo: { noIndex: true } }
  },
  {
    path: '/freight/preview/:id',
    name: 'FreightPreview',
    component: createAsyncComponent(() => import('../views/freight/FreightPreview.vue')),
    meta: { seo: { noIndex: true, title: '物流预览_蒙古丽人' } }
  },
  {
    path: '/freight/ship-orders/create',
    name: 'FreightShipOrderCreate',
    component: createAsyncComponent(() => import('../views/freight/FreightShipOrderCreate.vue')),
    meta: { seo: { noIndex: true, title: '创建物流订单_蒙古丽人' } }
  },
  {
    path: '/freight/create',
    name: 'FreightCreate',
    component: createAsyncComponent(() => import('../views/freight/FreightCreate.vue')),
    meta: { seo: { noIndex: true, title: '创建物流_蒙古丽人' } }
  },
  {
    path: '/freight/mine',
    name: 'MyFreights',
    component: createAsyncComponent(() => import('../views/freight/MyFreights.vue')),
    meta: { seo: { noIndex: true, title: '我的物流_蒙古丽人' } }
  },
  {
    path: '/freight/ship-orders/mine',
    name: 'MyShipOrders',
    component: createAsyncComponent(() => import('../views/freight/MyShipOrders.vue')),
    meta: { seo: { noIndex: true, title: '我的物流订单_蒙古丽人' } }
  },
  {
    path: '/freight',
    name: 'FreightPreviewQuery',
    component: createAsyncComponent(() => import('../views/freight/FreightPreview.vue')),
    meta: { seo: { noIndex: true, title: '物流查询_蒙古丽人' } }
  },
  {
    path: '/buyerorder',
    name: 'BuyerOrder',
    component: createAsyncComponent(() => import('../views/BuyerOrderPreview.vue')),
    meta: { seo: { noIndex: true, title: '买家订单_蒙古丽人' } }
  },

]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// 处理微信登录回调
async function handleWechatLoginCallback(code) {
  try {
    console.log('开始处理微信授权回调...')
    
    // 调用后端API进行微信登录
    const response = await wechatLogin(code)
    
    if (response.code === 1 && response.data) {
      const { token, nickname, headimgurl, uid, unionid, openid } = response.data
      
      // 构建用户信息对象
      const userInfo = {
        uid: uid,
        nickname: nickname || '微信用户',
        avatar: headimgurl || '',
        unionid: unionid,
        openid: openid
      }
      
      // 保存到localStorage
      localStorage.setItem('wechat_token', token)
      localStorage.setItem('wechat_user_info', JSON.stringify(userInfo))
      localStorage.setItem('wechat_uid', uid)
      
      // 更新Vuex状态
      store.dispatch('login', {
        user: userInfo,
        token: token
      })
      
      console.log('微信自动登录成功:', userInfo)
      Toast.success('微信登录成功！')
      
      return userInfo
    } else {
      throw new Error(response.msg || '登录失败')
    }
  } catch (error) {
    console.error('微信登录回调处理失败:', error)
    throw error
  }
}

/**
 * 根据路由meta信息动态更新页面SEO标签
 * @param {Object} seoMeta - 路由的seo配置
 */
function updateRouteSeo(seoMeta) {
  if (!seoMeta) return

  // 更新title
  if (seoMeta.title) {
    document.title = seoMeta.title
  }

  // 更新description
  if (seoMeta.description) {
    let descMeta = document.querySelector('meta[name="description"]')
    if (descMeta) {
      descMeta.setAttribute('content', seoMeta.description)
    }
  }

  // 更新keywords
  if (seoMeta.keywords) {
    let kwMeta = document.querySelector('meta[name="keywords"]')
    if (kwMeta) {
      kwMeta.setAttribute('content', seoMeta.keywords)
    }
  }

  // 处理noIndex：订单、支付等页面不应被搜索引擎索引
  let robotsMeta = document.querySelector('meta[name="robots"]')
  if (seoMeta.noIndex) {
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta')
      robotsMeta.setAttribute('name', 'robots')
      document.head.appendChild(robotsMeta)
    }
    robotsMeta.setAttribute('content', 'noindex, nofollow')
  } else {
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta')
      robotsMeta.setAttribute('name', 'robots')
      document.head.appendChild(robotsMeta)
    }
    robotsMeta.setAttribute('content', 'index, follow')
  }

  // 更新canonical
  let canonicalLink = document.querySelector('link[rel="canonical"]')
  const canonicalUrl = seoMeta.noIndex ? '' : `https://mall.sainbainu.com${window.location.pathname}`
  if (canonicalLink && seoMeta.noIndex) {
    canonicalLink.remove()
  } else if (!canonicalLink && canonicalUrl) {
    canonicalLink = document.createElement('link')
    canonicalLink.setAttribute('rel', 'canonical')
    canonicalLink.setAttribute('href', canonicalUrl)
    document.head.appendChild(canonicalLink)
  } else if (canonicalLink && canonicalUrl) {
    canonicalLink.setAttribute('href', canonicalUrl)
  }
}

router.beforeEach((to, from, next) => {
  // 更新路由级别SEO
  if (to.meta?.seo) {
    updateRouteSeo(to.meta.seo)
  }

  // 检查是否是微信授权回调
  const isWechatCallback = to.query.code && to.query.state
  
  // 注释掉自动微信授权登录逻辑，允许用户正常访问应用
  // 用户可以通过登录页面手动进行微信登录
  /*
  if (isWechat && !isLoggedIn && !isWechatCallback) {
    const appId = process.env.VUE_APP_WECHAT_MP_APP_ID || 'wx7478be49bad3b7e0'
    
    if (appId && appId !== 'wx7478be49bad3b7e0') {
      const state = Math.random().toString(36).substring(2, 15)
      localStorage.setItem('wechat_login_state', state)
      localStorage.setItem('wechat_redirect_path', to.fullPath)
      
      const currentUrl = window.location.origin + window.location.pathname
      const redirectUri = encodeURIComponent(currentUrl)
      const scope = 'snsapi_userinfo'
      const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&state=${state}#wechat_redirect`
      
      window.location.href = authUrl
      return
    }
  }
  */
  
  // 如果是微信授权回调，处理授权结果
  if (isWechatCallback) {
    // 验证state参数
    const savedState = localStorage.getItem('wechat_login_state')
    if (to.query.state === savedState) {
      // 清除state
      localStorage.removeItem('wechat_login_state')
      
      // 调用后端API处理授权码
      console.log('微信授权回调:', {
        code: to.query.code,
        state: to.query.state
      })
      
      // 异步处理微信登录
      handleWechatLoginCallback(to.query.code)
        .then(() => {
          // 登录成功后跳转
          const redirectPath = localStorage.getItem('wechat_redirect_path') || '/'
          localStorage.removeItem('wechat_redirect_path')
          
          // 使用replace方式跳转，清除URL中的授权参数
          window.location.replace(redirectPath)
        })
        .catch((error) => {
          console.error('微信登录失败:', error)
          Toast.fail('微信登录失败: ' + error.message)
          
          // 登录失败，跳转到首页
          const redirectPath = localStorage.getItem('wechat_redirect_path') || '/'
          localStorage.removeItem('wechat_redirect_path')
          window.location.replace(redirectPath)
        })
      
      // 先跳转到当前页面，避免路由阻塞
      next()
      return
    }
  }
  
  // URL 规范：全局不再使用时间戳（tm）等临时参数触发刷新
  // 原因：带时间戳的 URL 会被分享/抓取扩散，导致同一商品产生大量重复 URL，
  //       被搜索引擎判为重复页面，浪费抓取配额、稀释页面权重。
  // 需要「同路由不同参数」时刷新数据的场景（如商品详情页 A→B 切换），
  //       统一用组件内的 watch(() => route.query.xxx) 实现，
  //       参考 src/views/ProductDetail.vue。
  next();
});

export default router
